import {
  Component,
  OnInit,
  computed,
  signal,
  inject,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { DataViewModule } from 'primeng/dataview';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { CardModule } from 'primeng/card';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';

// Kanzi
import * as fromCustomer from '@kanzi-apes/kanzi-customer';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { LocationModel, LocationFilterModel } from '@kanzi-apes/kanzi-models';
import { WarehousesQueriesService } from '../../../queries';
import { Store } from '@ngrx/store';

@Component({
    selector: 'kanzi-apes-warehouse-list',
    imports: [
        CommonModule,
        TranslateModule,
        ButtonModule,
        DataViewModule,
        InputTextModule,
        SkeletonModule,
        TagModule,
        CardModule,
        ConfirmDialogModule,
        TooltipModule,
        KanziUiModule,
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './warehouse-list.component.html',
    styleUrls: ['./warehouse-list.component.scss']
})
export class WarehouseListComponent implements OnInit {
  // Inyección de servicios
  private router = inject(Router);
  private queries = inject(WarehousesQueriesService);
  private confirmService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private store = inject(Store);
  customerSelected = this.store.selectSignal(fromCustomer.getCustomerSelected);

  // Signals para filtros
  filters = signal<LocationFilterModel | null>(null);

  // Signal para controlar cuándo ejecutar la query
  isQueryEnabled = signal<boolean>(false);

  customerEffect = effect(
    () => {
      const customer = this.customerSelected();
      console.log('[WarehouseList] Customer selected:', customer);
      
      // Siempre habilitar el query, con o sin customer
      const newFilters: LocationFilterModel = {
        page: 1,
        page_size: 50,
      };
      
      // Si hay customer, agregarlo a los filtros
      if (customer) {
        newFilters.customer_id = customer.id;
      }
      
      console.log('[WarehouseList] Setting filters:', newFilters);
      this.filters.set(newFilters);
      this.isQueryEnabled.set(true);
      console.log('[WarehouseList] Query enabled');
    },
    { allowSignalWrites: true }
  );

  searchText = signal<string>('');

  // Query para lista de warehouses - solo se ejecuta cuando isQueryEnabled es true
  warehousesQuery = this.queries.useWarehousesList(
    this.filters,
    this.isQueryEnabled
  );

  // Computed signals para la UI
  get warehouses(): LocationModel[] {
    return this.warehousesQuery.data()?.results || [];
  }

  totalRecords = computed(() => this.warehousesQuery.data()?.count || 0);
  isLoading = computed(
    () => this.warehousesQuery.isPending() || this.warehousesQuery.isFetching()
  );

  // Mutations
  deleteMutation = this.queries.useDeleteWarehouse();

  constructor() {
    console.log('[WarehouseList] Constructor called');
  }

  ngOnInit(): void {
    console.log('[WarehouseList] Component initialized');
    console.log('[WarehouseList] Customer selected:', this.customerSelected());
    console.log('[WarehouseList] Filters:', this.filters());
    console.log('[WarehouseList] Query enabled:', this.isQueryEnabled());
    // Query se ejecuta automáticamente
  }

  /**
   * Navegar a crear nuevo warehouse
   */
  onCreateWarehouse(): void {
    this.router.navigate(['locations', 'warehouses', 'create']);
  }

  /**
   * Navegar a detalle de warehouse
   */
  onViewWarehouse(warehouse: LocationModel): void {
    if (warehouse.id) {
      this.router.navigate(['locations', 'warehouses', 'detail', warehouse.id]);
    }
  }

  /**
   * Navegar a editar warehouse
   */
  onEditWarehouse(warehouse: LocationModel, event?: Event): void {
    event?.stopPropagation();
    if (warehouse.id) {
      this.router.navigate(['locations', 'warehouses', 'edit', warehouse.id]);
    }
  }

  /**
   * Eliminar warehouse con confirmación
   */
  onDeleteWarehouse(warehouse: LocationModel, event?: Event): void {
    event?.stopPropagation();

    this.confirmService.confirm({
      message: `¿Está seguro de eliminar el warehouse "${warehouse.name}"?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (warehouse.id) {
          this.deleteMutation.mutate(warehouse.id, {
            onSuccess: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Warehouse eliminado',
                detail: `El warehouse "${warehouse.name}" fue eliminado exitosamente`,
              });
            },
            onError: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error al eliminar',
                detail:
                  error.message || 'Ocurrió un error al eliminar el warehouse',
              });
            },
          });
        }
      },
    });
  }

  /**
   * Buscar warehouses por texto
   */
  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    const search = input.value.trim();

    this.searchText.set(search);

    // Actualizar filtros (cleanFilters remueve valores vacíos)
    this.filters.set({
      ...this.filters(),
      page: 1,
      search: search,
    });
  }

  /**
   * Limpiar búsqueda
   */
  onClearSearch(): void {
    this.searchText.set('');
    const { search, ...rest } = this.filters() || {};
    this.filters.set({
      ...rest,
      page: 1,
    });
  }

  /**
   * Cambiar página
   */
  onPageChange(page: number): void {
    this.filters.set({
      ...this.filters(),
      page: page,
    });
  }

  /**
   * Pre-cargar warehouse al hacer hover (mejora UX)
   */
  onWarehouseHover(warehouse: LocationModel): void {
    if (warehouse.id) {
      this.queries.prefetchWarehouse(warehouse.id);
    }
  }
}
